import {
  ResponsiveContainer,
  ScatterChart,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  Scatter,
  Cell,
  CartesianGrid,
  ReferenceArea,
} from 'recharts';

import { useMemo } from 'react';
import type { DocumentReconstructedDocumentsListItem } from '@avisoma/shared';
import type { ProcessingDataClassification } from '@avisoma/shared/dist/ProcessingData';
import { ScatterPagePreviewTooltip } from './ScatterPagePreviewTooltip';
import { classificationChartSize } from './classificationChartSize';

type Entry = {
  id: string;
  classification: ProcessingDataClassification;
};

const UKNOWN_CLASSIFICATION = '?';

// DR-2427: past this page count the per-page scatter (one <Cell> + one x-axis tick per page,
// plus one <ReferenceArea> per reconstructed doc) blows past the recharts scatter ceiling and
// freezes the tab. Above it we bin pages by index and aggregate per bin per category, keeping
// the rendered node count bounded. At or below it the chart renders exactly as before.
const DECIMATION_THRESHOLD = 1000;
// Upper bound on rendered aggregated points. Bins are capped so bins × categories stays under
// this regardless of page count, keeping us clear of the recharts scatter ceiling.
const MAX_DECIMATED_POINTS = 1200;
const MAX_BINS = 1000;
const DECIMATED_X_TICKS = 12;

type ChartPoint = {
  id: string;
  page: Entry;
  pageNumber: number;
  classification: string;
  confidence: number;
  size: number;
  reconstructionHint?: string;
  /** Aggregated-bin fields (decimated mode only; absent on per-page points). */
  count?: number;
  pageRange?: string;
};

export function DocumentPagesClassificationChart({
  pages = [],
  reconstructedDocuments,
  onPageClick,
}: {
  pages: Entry[] | undefined;
  reconstructedDocuments?: DocumentReconstructedDocumentsListItem[];
  onPageClick?: (page: Entry) => void;
}) {
  const isDecimated = pages.length > DECIMATION_THRESHOLD;

  const pageCategories = useMemo(() => {
    return Array.from(new Set(pages.map((page) => page.classification?.[0]?.[0] ?? UKNOWN_CLASSIFICATION))).sort(
      (a, b) => a.localeCompare(b),
    );
  }, [pages]);

  const groupIndexByPageIndex = useMemo(
    () =>
      buildGroupIndexByPageIndex(
        pages.map((p) => p.id),
        reconstructedDocuments,
      ),
    [pages, reconstructedDocuments],
  );

  const chartData = useMemo<ChartPoint[]>(
    () =>
      isDecimated
        ? buildDecimatedChartData(pages, pageCategories.length)
        : pages
            .map((page, index) => {
              const groupIndex = groupIndexByPageIndex[index];
              const doc = groupIndex !== undefined ? reconstructedDocuments?.[groupIndex] : undefined;
              const reconstructionHint =
                groupIndex !== undefined && doc !== undefined && reconstructedDocuments !== undefined
                  ? `Virtual doc ${groupIndex + 1} of ${reconstructedDocuments.length} · pages ${doc.startPageIndex + 1}–${doc.endPageIndex + 1}`
                  : undefined;

              return {
                id: page.id,
                // Keep the original entry so a <Cell> mapped over the SORTED chartData
                // can still resolve its page (for onClick) — chartData itself flattens
                // classification to a string.
                page,
                pageNumber: index + 1,
                classification: page.classification?.[0]?.[0] ?? UKNOWN_CLASSIFICATION,
                confidence: page.classification?.[0]?.[1] ?? 0,
                size: classificationChartSize(page.classification),
                reconstructionHint,
              };
            })
            .sort((a, b) => a.classification.localeCompare(b.classification)),
    [isDecimated, pages, pageCategories.length, groupIndexByPageIndex, reconstructedDocuments],
  );

  // One tick per page reads fine for small docs; for a decimated chart that would be thousands
  // of ticks, so space a fixed number across the page range instead.
  const xTicks = useMemo(
    () =>
      isDecimated
        ? Array.from(
            new Set(
              Array.from({ length: DECIMATED_X_TICKS }, (_, i) =>
                Math.max(1, Math.round((pages.length * (i + 1)) / DECIMATED_X_TICKS)),
              ),
            ),
          )
        : Array.from({ length: pages.length }, (_, i) => i + 1),
    [isDecimated, pages.length],
  );

  const yBand =
    pageCategories.length >= 1
      ? {
          y1: pageCategories[0],
          y2: pageCategories.length > 1 ? pageCategories[pageCategories.length - 1] : pageCategories[0],
        }
      : null;

  return (
    <ResponsiveContainer width="100%" height="100%" className={'min-h-[400px]'}>
      <ScatterChart accessibilityLayer={false} data={chartData} margin={{ top: 20, right: 30, left: 40, bottom: 10 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0} />
        {/* One shaded band per reconstructed doc — skipped when decimated (thousands of ~1px
            bands merge into an unreadable wash and blow the node budget). */}
        {!isDecimated &&
          yBand &&
          reconstructedDocuments?.map((doc) => (
            <ReferenceArea
              key={doc.reconstructedDocumentId}
              x1={doc.startPageIndex + 1}
              x2={doc.endPageIndex + 1}
              y1={yBand.y1}
              y2={yBand.y2}
              fill={'hsl(0% 0% 10%)'}
              fillOpacity={0.1}
              strokeOpacity={0}
            />
          ))}
        <XAxis
          dataKey="pageNumber"
          type="number"
          label={{
            value: 'Page Number',
            position: 'insideBottom',
            offset: -10,
          }}
          domain={[0, pages.length + 1]}
          ticks={xTicks}
          allowDuplicatedCategory={false}
        />
        <YAxis
          dataKey="classification"
          type="category"
          allowDuplicatedCategory={false}
          domain={pageCategories}
          tickFormatter={(classification: string) => {
            const parts = classification.replace(/[a-z ]/g, '');
            return parts.length > 2 ? parts : classification;
          }}
        />
        <ZAxis type="number" dataKey="size" range={[50, 900]} name="size" />
        <Tooltip
          content={
            <ScatterPagePreviewTooltip
              pageIdKey="id"
              stats={
                isDecimated
                  ? [
                      { key: 'classification', label: '', format: (v) => String(v), bold: true },
                      { key: 'count', label: 'Pages in bin', format: (v) => String(v), optional: true },
                      { key: 'pageRange', label: 'Pages', optional: true },
                      { key: 'confidence', label: 'Avg confidence', format: (v) => `${((v as number) * 100).toFixed(0)}%` },
                    ]
                  : [
                      { key: 'classification', label: '', format: (v) => String(v), bold: true },
                      { key: 'confidence', label: 'Confidence', format: (v) => `${((v as number) * 100).toFixed(0)}%` },
                      { key: 'size', label: 'NCD', format: (v) => `${(v as number).toFixed(1)}%` },
                      { key: 'reconstructionHint', label: '', optional: true },
                    ]
              }
            />
          }
        />
        <Scatter dataKey="classification" fill="#4F46E5" shape="circle" fillOpacity={0.6} cursor="pointer">
          {chartData.map((entry) => (
            <Cell
              key={`cell-${entry.id}`}
              onClick={() => {
                onPageClick?.(entry.page);
              }}
              fill={`hsl(${((Math.max(0, entry.confidence - 0.7) * 100) / 20) * 120}, 100%, 50%)`}
            />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
}

/**
 * DR-2427: aggregate pages into fixed-width bins by page index, grouped per category, so a
 * huge document renders a bounded number of points instead of one per page. Bin count is
 * capped so `bins × categories` stays under MAX_DECIMATED_POINTS. Each rendered point sits at
 * its bin's first matching page (an actual page position, so click/preview still resolve a
 * real page), carries the average confidence for its colour, and the page count for its size.
 */
function buildDecimatedChartData(pages: Entry[], categoryCount: number): ChartPoint[] {
  const binCount = Math.min(MAX_BINS, Math.max(1, Math.ceil(MAX_DECIMATED_POINTS / Math.max(1, categoryCount))));
  const binSize = Math.max(1, Math.ceil(pages.length / binCount));

  type Bin = {
    bin: number;
    category: string;
    count: number;
    confidenceSum: number;
    firstPage: Entry;
    firstIndex: number;
    lastIndex: number;
  };
  const groups = new Map<string, Bin>();

  pages.forEach((page, index) => {
    const category = page.classification?.[0]?.[0] ?? UKNOWN_CLASSIFICATION;
    const confidence = page.classification?.[0]?.[1] ?? 0;
    const bin = Math.floor(index / binSize);
    const key = `${bin} ${category}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      existing.confidenceSum += confidence;
      existing.lastIndex = index;
    } else {
      groups.set(key, { bin, category, count: 1, confidenceSum: confidence, firstPage: page, firstIndex: index, lastIndex: index });
    }
  });

  return Array.from(groups.values())
    .map((g) => ({
      id: g.firstPage.id,
      page: g.firstPage,
      pageNumber: g.firstIndex + 1,
      classification: g.category,
      confidence: g.confidenceSum / g.count,
      size: g.count,
      count: g.count,
      pageRange: g.firstIndex === g.lastIndex ? `${g.firstIndex + 1}` : `${g.firstIndex + 1}–${g.lastIndex + 1}`,
    }))
    .sort((a, b) => a.classification.localeCompare(b.classification));
}

function buildGroupIndexByPageIndex(
  orderedPageIds: string[],
  reconstructedDocuments: DocumentReconstructedDocumentsListItem[] | undefined,
): (number | undefined)[] {
  if (!reconstructedDocuments?.length) {
    return orderedPageIds.map(() => undefined);
  }

  const indexById = new Map(orderedPageIds.map((id, i) => [id, i]));
  const out: (number | undefined)[] = orderedPageIds.map(() => undefined);
  reconstructedDocuments.forEach((doc, groupIndex) => {
    for (const pageId of doc.pageIds) {
      const idx = indexById.get(pageId);
      if (idx !== undefined) {
        out[idx] = groupIndex;
      }
    }
  });
  return out;
}

